interface CrownLoaderProps {
  /** Diameter in px. Default: 80 */
  size?: number;
  /** Spinner accent color. Default: '#9c7c54' (brand gold) */
  color?: string;
  /** Ring thickness in px. Default: derived from size */
  strokeWidth?: number;
  /** Optional loading message displayed below the spinner */
  message?: string;
  /** Render as a full-page centered overlay. Default: false */
  fullPage?: boolean;
  /**
   * Retained for backward compatibility with existing call sites that pass
   * the old crown-animation flags. They are no-ops now — the loader is a
   * simple spinner with no crown animation.
   */
  playOnce?: boolean;
  onHover?: boolean;
  /** Additional CSS classes on the wrapper */
  className?: string;
}

/**
 * Loading spinner for Idaho Pageants.
 *
 * The animated crown was retired in favor of a minimal, accessible spinner.
 * The props API is preserved (size, color, message, fullPage, className) so
 * existing call sites continue to work unchanged; the former crown-animation
 * flags (`playOnce`, `onHover`) are accepted but ignored.
 */
export default function CrownLoader({
  size = 80,
  color = '#9c7c54',
  strokeWidth,
  message,
  fullPage = false,
  className = '',
}: CrownLoaderProps) {
  const borderWidth = strokeWidth ?? Math.max(2, Math.round(size / 16));

  const spinner = (
    <span
      role="status"
      aria-label="Loading"
      className="inline-block animate-spin rounded-full"
      style={{
        width: size,
        height: size,
        borderWidth,
        borderStyle: 'solid',
        borderColor: color,
        borderTopColor: 'transparent',
      }}
    />
  );

  if (fullPage) {
    return (
      <div
        className={`flex flex-col items-center justify-center min-h-[60vh] ${className}`.trim()}
        data-testid="crown-loader"
      >
        {spinner}
        {message && (
          <p className="mt-4 font-sans text-sm text-gold-700 tracking-wide">
            {message}
          </p>
        )}
      </div>
    );
  }

  return (
    <div
      className={`flex flex-col items-center justify-center ${className}`.trim()}
      data-testid="crown-loader"
    >
      {spinner}
      {message && (
        <p className="mt-2 font-sans text-xs text-gold-700 tracking-wide">
          {message}
        </p>
      )}
    </div>
  );
}
